import React, { useEffect, useMemo, useState } from 'react';
import Logo from '@/assets/logos/logo.svg';
import FormItem from '@/src/components/molecule/FormItem';
import { useForm } from 'react-hook-form';
import Button from '@/src/components/atom/Button';
import { useNavigate } from 'react-router-dom';
import { localstorageKeySet } from '@/src/constants/localstorage';
import { CheckboxWithText } from '@/src/components/molecule/CheckboxWithText';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useMutation } from '@tanstack/react-query';
import { postSignInAPI } from '@/src/apis/rest/administrators';
import { PostSignInRequestBody } from '@/src/apis/rest/administrators.dtos';
import useAuthStore from '@/src/store/useAuthStore';
import { AdministratorDto } from '@/src/models/AdministratorDto';
import useDialog, { DialogType } from '@/hooks/useDialog';
import { LoginErrorCode } from '@/src/pages/Login/type';

function LoginPage() {
  const navigate = useNavigate();
  const auth = useAuthStore(state => state.auth);
  const { setAuth, setToken } = useAuthStore();
  const [errorCode, setErrorCode] = useState<LoginErrorCode>(
    'INVALID_OLD_PASSWORD',
  );

  const {
    control,
    handleSubmit,
    setFocus,
    getValues,
    watch,
    formState: { errors },
  } = useForm<{
    aId: string;
    password: string;
  }>({
    defaultValues: {
      aId: localStorage.getItem(localstorageKeySet.ECOYA_ADMIN_ID) ?? '',
      password: '',
    },
  });

  const {
    Dialog: ErrorDialog,
    openDialog,
    closeDialog,
    isOpen,
  } = useDialog({ type: DialogType.ALERT });

  useEffect(() => {
    if (auth && !auth.account.isTemporaryPassword) {
      navigate(PATH.ROOT);
    }
    if (auth && auth.account.isTemporaryPassword) {
      navigate(PATH.CHANGE_PASSWORD);
    }
  }, [auth, navigate]);

  useEffect(() => {
    setFocus('aId');
  }, [setFocus]);

  const [isRememberId, setIsRememberId] = useState<boolean>(
    !!localStorage.getItem(localstorageKeySet.ECOYA_ADMIN_ID),
  );

  const isFormValid =
    !!watch('aId').trim() &&
    !!watch('password').trim() &&
    !errors.aId &&
    !errors.password;

  const postSignIn = useMutation({
    mutationFn: (params: PostSignInRequestBody) => postSignInAPI(params),
  });

  const errorMessagesMap = useMemo(() => {
    return {
      INVALID_OLD_PASSWORD: {
        title: '로그인 실패 안내',
        description:
          '아이디 또는 비밀번호를 다시 확인해주세요\n5회 이상 틀릴 경우 로그인이 제한됩니다.',
      },
      ADMINISTRATOR_NOT_FOUND: {
        title: '로그인 실패 안내',
        description:
          '아이디 또는 비밀번호를 다시 확인해주세요\n5회 이상 틀릴 경우 로그인이 제한됩니다.',
      },
      ADMINISTRATOR_LOCKED: {
        title:
          '비밀번호를 5회 이상 잘못 입력하여\n계정이 잠금상태로 전환되었어요',
        description:
          '관리자에게 문의하여 비밀번호 초기화 후 로그인이 가능해요.',
      },
    } as const;
  }, []);

  const handleSubmitClick = async () => {
    const params = {
      aId: getValues('aId'),
      password: getValues('password'),
    };
    try {
      const res = await postSignIn.mutateAsync(params);
      setAuth(res.auth as AdministratorDto);
      setToken(res.token);

      if (isRememberId) {
        localStorage.setItem(localstorageKeySet.ECOYA_ADMIN_ID, params.aId);
      } else {
        localStorage.setItem(localstorageKeySet.ECOYA_ADMIN_ID, '');
      }
    } catch (e: any) {
      setErrorCode(e?.error || 'INVALID_OLD_PASSWORD');
      openDialog();
    }
  };

  return (
    <>
      <ErrorDialog
        className="whitespace-pre-wrap"
        open={isOpen}
        onOpenChange={closeDialog}
        title={
          <div className="whitespace-pre-wrap">
            {errorMessagesMap[errorCode]?.title}
          </div>
        }
        cancelButtonProps={{ className: 'hidden' }}
      >
        <div className="whitespace-pre-wrap">
          {errorMessagesMap[errorCode]?.description}
        </div>
      </ErrorDialog>
      <div className="flex h-screen items-center">
        <main className="flex h-full w-full flex-1 flex-col items-center justify-center">
          <div className="flex items-center ">
            <form
              onSubmit={handleSubmit(handleSubmitClick)}
              className="flex w-[482px] flex-col gap-y-10"
            >
              <div>
                <img src={Logo} alt="logo" className="" />
              </div>

              <section className="flex flex-col gap-y-6">
                <FormItem
                  label="아이디(이메일)"
                  labelSize="M"
                  name="aId"
                  type="text"
                  control={control}
                  direction="vertical"
                  inputProps={{
                    placeholder: '아이디(이메일) 입력',
                    onKeyDown: e => {
                      if (e.key === 'Enter' && isFormValid && !isOpen)
                        handleSubmit(handleSubmitClick)();
                    },
                  }}
                  isRequiredHidden={true}
                  hasShadow={true}
                />
                <FormItem
                  label="비밀번호"
                  labelSize="M"
                  name="password"
                  type="text"
                  control={control}
                  direction="vertical"
                  inputProps={{
                    type: 'password',
                    placeholder: '비밀번호',
                    autoComplete: 'new-password',
                    allowClear: false,
                    onKeyDown: e => {
                      if (e.key === 'Enter' && isFormValid && !isOpen)
                        handleSubmit(handleSubmitClick)();
                    },
                  }}
                  isRequiredHidden={true}
                  hasShadow={true}
                />

                <CheckboxWithText
                  checked={isRememberId}
                  value={isRememberId.toString()}
                  labelClassName="ml-1"
                  onClick={() => {
                    setIsRememberId(!isRememberId);
                  }}
                  label="아이디(이메일) 저장하기"
                />
              </section>

              <Button
                className="w-full bg-primary"
                type="submit"
                disabled={!isFormValid}
                isLoading={postSignIn.isPending}
              >
                {BUTTON_CONST.login}
              </Button>
            </form>
          </div>
          <footer className="mt-[40px] px-[10px] text-center">
            <span className="text-b13r text-gray-400">
              Copyright ⓒ 2024 HRM Corporation
            </span>
          </footer>
        </main>
      </div>
    </>
  );
}

export default LoginPage;
